// Angular Core
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// RxJS
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

// DevExtreme
import {
  DxButtonModule,
  DxTextBoxModule,
  DxContextMenuModule,
  DxPopupModule,
  DxDateBoxModule,
  DxTagBoxModule,
  DxSelectBoxModule,
  DxCheckBoxModule,
} from 'devextreme-angular';
import { DxoButtonOptionsModule } from 'devextreme-angular/ui/nested';

// Application Types
import { SparkBoardFilterState, ProjectHubSpark, tagColor, tagColors } from '../project-hub.types';

// Application Components
import { PinnedSparkCardComponent } from '../components/pinned-spark/pinned-spark.component';
import { SparkCardComponent } from '../components/spark-card/spark-card.component';
import { SparkFilterComponent } from '../components/spark-filter/spark-filter.component';
import { SparkEditorComponent } from '../components/spark-editor/spark-editor.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';

// Application Services
import { ProjectHubService } from 'src/app/services/project-hub.service';
import { ConfigService } from 'src/app/services/config.service';
import { GlobalPopupService } from 'src/app/components/generic-popup/presets/global-popup/global-popup.service';

// Application Globals
import { AppGlobals } from 'src/app/app.globals';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SparkEditorTiptapComponent } from '../components/spark-editor-tiptap/spark-editor-tiptap.component';

@Component({
    selector: 'app-project-hub-board',
    imports: [
        CommonModule,
        FormsModule,
        PinnedSparkCardComponent,
        SparkCardComponent,
        DxTextBoxModule,
        DxButtonModule,
        DxContextMenuModule,
        SvgIconComponent,
        GpdButtonComponent,
        DxPopupModule,
        DxDateBoxModule,
        DxoButtonOptionsModule,
        DxTagBoxModule,
        DxSelectBoxModule,
        DxCheckBoxModule,
        SparkFilterComponent,
        SparkEditorTiptapComponent,
    ],
    templateUrl: './project-hub-board.component.html',
    styleUrls: ['./project-hub-board.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class ProjectHubBoardComponent implements OnInit, OnDestroy, AfterContentInit {
  // ===== DATA PROPERTIES =====
  sparks: ProjectHubSpark[] = [];
  displayedSparks: ProjectHubSpark[] = [];
  pinnedSparks: ProjectHubSpark[] = [];

  // ===== BOARD PROPERTIES =====
  boardId: number | null = null;
  newSparks: number = 0;

  // ===== PAGINATION PROPERTIES =====
  readonly SPARKS_PER_PAGE = 5;
  currentPage = 1;
  totalCount = 0;
  hasMoreSparks = false;

  // ===== LOADING STATES =====
  loadingSparks = false;
  loadingBoard = false;

  // ===== SEARCH PROPERTIES =====
  searchTerm = '';
  private searchTimeout: any;

  // ===== FILTERING PROPERTIES =====
  filterState: SparkBoardFilterState = {
    period: { from: null, to: null },
    tags: [],
    person: null,
    unread: false,
  };

  // ===== EDITING PROPERTIES =====
  editingCard: ProjectHubSpark | null = null;

  // ===== UI STATE PROPERTIES =====
  expandedCardIndex: number | null = null;
  selectedCard: ProjectHubSpark | null = null;
  readonly SPARK_CARD_WIDTH = 300;
  readonly SPARK_CARD_GAP = 16;

  // ===== TAG PROPERTIES =====
  sparkTags: { id: number; name: string; color?: tagColor }[] = [];
  sparkTagMap: {
    [key: string]: { id: number; name: string; color?: tagColor };
  } = {};

  // ===== USER PROPERTIES =====
  users: any[] = [];
  userMap: { [key: string]: string } = {};
  private userListSubscription: Subscription = new Subscription();
  private routeParamsSubscription: Subscription = new Subscription();

  @ViewChildren('sparkCard', { read: ElementRef })
  sparkCards!: QueryList<ElementRef>;
  @ViewChildren('sparkGrid', { read: ElementRef })
  sparkGrid!: QueryList<ElementRef>;

  @Output() sparkRead = new EventEmitter<ProjectHubSpark>();

  constructor(
    private projectHubService: ProjectHubService,
    private configService: ConfigService,
    private route: ActivatedRoute,
    private popupService: GlobalPopupService,
    private cdr: ChangeDetectorRef
  ) {
    this.boardId = parseInt(this.route.snapshot.paramMap.get('boardId'));
    if (this.boardId) localStorage.setItem('projectHubBoardId', this.boardId.toString());
  }

  ngOnInit(): void {
    this.subscribeToUserList();
    this.loadSparkTags();
    const prefilledSearch = this.route.snapshot.queryParamMap.get('q');
    if (prefilledSearch) {
      this.searchTerm = prefilledSearch;
    }
    this.loadBoardData();
    this.routeParamsSubscription = this.route.params.subscribe(params => {
      const newBoardId = +params['boardId'];
      if (this.boardId !== newBoardId) {
        this.boardId = newBoardId;
        this.searchTerm = this.route.snapshot.queryParamMap.get('q') ?? '';
        this.loadBoardData();
        // Reset any stale UI state
        this.selectedCard = null;
        this.expandedCardIndex = null;
        this.editingCard = null;
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions to prevent memory leaks
    this.userListSubscription.unsubscribe();
    this.routeParamsSubscription.unsubscribe();
  }

  private subscribeToUserList(): void {
    this.userListSubscription = this.configService.userList$.subscribe(users => {
      this.users = users;
      this.userMap = users.reduce(
        (acc, user) => {
          acc[user.emailAddress] = user.displayName;
          return acc;
        },
        {} as { [key: string]: string }
      );
    });

    // Trigger the initial load if the user list is empty
    if (this.users.length === 0) {
      this.configService.getListOfUsers({});
    }
  }
  ngAfterContentInit() {}

  async loadBoardData() {
    try {
      if (!this.boardId) {
        return;
      }

      this.loadingBoard = true;

      // Reset pagination state
      this.currentPage = 1;
      this.displayedSparks = [];
      this.pinnedSparks = [];

      const response = await this.projectHubService.GetBoard(
        this.boardId,
        this.currentPage,
        this.SPARKS_PER_PAGE,
        this.filterState,
        this.searchTerm || undefined
      );
      if (!response.hasError) {
        this.sparks = response.sparks;
        this.pinnedSparks = response.pinnedSparks || [];
        this.displayedSparks = [...response.sparks];
        this.totalCount = response.totalCount || 0;
        this.hasMoreSparks = this.displayedSparks.length < this.totalCount;
        this.newSparks = response.unreadSparksCount;
      } else {
        AppGlobals.ShowErrorMessage(response.feedback);
      }
    } catch (err) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    } finally {
      this.loadingBoard = false;
    }
  }

  async loadSparkTags() {
    try {
      const result = await this.projectHubService.getSparkTags({});
      if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
      else {
        this.sparkTags = result.sparkTags;
        this.sparkTags.forEach((tag, index) => {
          tag.color = tagColors[index % tagColors.length];
        });
        this.sparkTagMap = this.sparkTags.reduce(
          (acc, tag) => {
            acc[tag.id] = tag;
            return acc;
          },
          {} as {
            [key: string]: { id: number; name: string; color?: tagColor };
          }
        );
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    }
  }

  async onSparkMove(event: { spark: ProjectHubSpark; targetBoardId: number }) {
    if (event.spark.id != null) {
      try {
        const result = await this.projectHubService.MoveSpark(event.spark.id, event.targetBoardId);
        if (result.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
        } else {
          // Optionally reload board or update UI state here
          this.loadBoardData();
        }
      } catch (error) {
        AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
      }
    }
  }

  async getUsers() {
    try {
      const result = await this.configService.getListOfUsers({});
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.users = Array.from(
          result.projectsUsers.map(x => {
            return {
              displayName: x.displayName,
              emailAddress: x.emailAddress,
            };
          })
        );
        this.userMap = this.users.reduce(
          (acc, user) => {
            acc[user.email] = user.displayName;
            return acc;
          },
          {} as { [key: string]: string }
        );
      }
    } catch (e) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    }
  }

  onSparkClick(clickedIndex: number) {
    if (this.selectedCard) {
      if (this.pinnedSparks[clickedIndex] === this.selectedCard) {
        this.expandedCardIndex = null;
        this.selectedCard = null;
        return;
      }
    }
    this.onSparkRead(this.pinnedSparks[clickedIndex].id);
    this.selectedCard = this.pinnedSparks[clickedIndex];
    var t = this.sparkGrid.first.nativeElement.getBoundingClientRect();
    let itemsPerRow = Math.floor((t.width + this.SPARK_CARD_GAP) / (this.SPARK_CARD_WIDTH + this.SPARK_CARD_GAP));
    const currentRow = Math.floor(clickedIndex / itemsPerRow) + 1;
    this.expandedCardIndex = Math.min(currentRow * itemsPerRow, this.pinnedSparks.length);
    this.sparkCards.get(clickedIndex)?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  onSparkAddClick() {
    this.editingCard = {
      id: null,
      userEmail: 'New Author',
      content: {
        text: '',
        files: {},
      },
      tags: [],
    };
  }

  async onSparkEditClick(card: ProjectHubSpark) {
    this.editingCard = { ...card };
    // Load the card contents directly into the editor component
    if (this.editingCard) {
      this.editingCard = {
        ...card,
        content: {
          text: this.editingCard.content.text,
          files: { ...this.editingCard.content.files },
        },
      };
    }
  }

  onSparkCancelClick() {
    this.editingCard = null;
  }

  onSparkEdit(spark: ProjectHubSpark) {
    this.onSparkEditClick(spark);
  }

  onSparkDelete(spark: ProjectHubSpark) {
    this.popupService.showDeletePopup({
      title: $localize`:@@deleteSparkTitle:Delete Spark`,
      content: $localize`:@@deleteSparkContent:Are you sure you want to delete this spark?`,
      onConfirm: () => {
        this.deleteSparkCard(spark);
      },
    });
  }

  onSparkPin(spark: ProjectHubSpark) {
    this.toggleSparkPin(spark, true);
  }

  onSparkUnpin(spark: ProjectHubSpark) {
    this.toggleSparkPin(spark, false);
  }

  onSparkRead(sparkId: number) {
    this.markSparkAsRead(sparkId);
  }

  onSparkLiked(event: { sparkId: number; isLiked: boolean }) {
    this.toggleSparkLike(event.sparkId, event.isLiked);
  }

  private async deleteSparkCard(spark: ProjectHubSpark) {
    try {
      const updatedSpark: ProjectHubSpark = { ...spark, isDeleted: true };
      const response = await this.projectHubService.WriteSpark(this.boardId, updatedSpark);
      if (response.hasError) {
        AppGlobals.ShowErrorMessage(response.feedback);
      } else {
        await this.loadBoardData();
        this.projectHubService.sparkDeletedSubject.next();
      }
    } catch (err) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    }
  }

  private async toggleSparkPin(spark: ProjectHubSpark, pinned: boolean) {
    try {
      const updatedSpark = { ...spark, pinned };
      const response = await this.projectHubService.WriteSpark(this.boardId, updatedSpark);
      if (response.hasError) {
        AppGlobals.ShowErrorMessage(response.feedback);
      } else {
        await this.loadBoardData();
      }
    } catch (err) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    }
  }

  async loadMoreSparks() {
    if (this.loadingSparks || !this.hasMoreSparks || !this.boardId) return;

    this.loadingSparks = true;
    this.currentPage++;

    try {
      const response = await this.projectHubService.GetBoard(
        this.boardId,
        this.currentPage,
        this.SPARKS_PER_PAGE,
        this.filterState,
        this.searchTerm || undefined
      );
      if (!response.hasError) {
        this.displayedSparks = [...this.displayedSparks, ...response.sparks];
        this.hasMoreSparks = this.displayedSparks.length < this.totalCount;
      } else {
        AppGlobals.ShowErrorMessage(response.feedback);
        this.currentPage--; // Revert page increment on error
      }
    } catch (err) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
      this.currentPage--; // Revert page increment on error
    } finally {
      this.loadingSparks = false;
    }
  }

  async onSparkReadAllClick() {
    if (!this.newSparks) return;
    this.newSparks = 0;
    this.projectHubService.setNewSparksMap({
      ...this.getCurrentNewSparksMap(),
      [this.boardId]: 0,
    });
    try {
      const response = await this.projectHubService.MarkAllSparksAsRead(this.boardId);
      if (response.hasError) {
        AppGlobals.ShowErrorMessage(response.feedback);
        this.loadBoardData();
        return;
      }
      // Create new array references to trigger OnPush change detection
      this.sparks = this.sparks.map((spark) => ({ ...spark, isNew: false }));
      this.pinnedSparks = this.pinnedSparks.map((spark) => ({
        ...spark,
        isNew: false,
      }));
      this.displayedSparks = this.displayedSparks.map((spark) => ({
        ...spark,
        isNew: false,
      }));
      this.projectHubService.setNewSparksMap({
        ...this.getCurrentNewSparksMap(),
        [this.boardId]: 0,
      });
    } catch (err) {
      this.loadBoardData();
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    }
    this.cdr.detectChanges();
  }

  onSearchValueChanged(event: any) {
    this.searchTerm = event.value || '';

    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search to avoid too many API calls
    this.searchTimeout = setTimeout(() => {
      this.performSearch();
    }, 500);
  }

  private async performSearch() {
    // Reset pagination when searching
    this.currentPage = 1;
    this.displayedSparks = [];

    await this.loadBoardData();
  }

  clearSearch() {
    this.searchTerm = '';
    this.performSearch();
  }

  // Event handlers for rich text editor component
  onSparkSaved() {
    this.editingCard = null;
    this.loadBoardData();
  }

  onSparkCancelled() {
    this.editingCard = null;
  }

  // Helper to get current value of newSparksMap$ (assuming it's a BehaviorSubject)
  private getCurrentNewSparksMap(): { [boardId: number]: number } {
    let currentMap: { [boardId: number]: number } = {};
    const sub = this.projectHubService.newSparksMap$.subscribe(map => {
      currentMap = map;
    });
    sub.unsubscribe();
    return currentMap;
  }

  // Spark interaction methods
  async markSparkAsRead(sparkId: number) {
    this.displayedSparks.forEach(spark => {
      if (spark.id === sparkId) {
        spark.isNew = false;
      }
    });
    this.pinnedSparks.forEach(spark => {
      if (spark.id === sparkId) {
        spark.isNew = false;
      }
    });
    this.projectHubService.setNewSparksMap({
      ...this.getCurrentNewSparksMap(),
      [this.boardId]: (this.getCurrentNewSparksMap()[this.boardId] || 0) - 1,
    });
    this.newSparks = this.newSparks - 1;

    try {
      const result = await this.projectHubService.MarkSparkAsRead(sparkId);
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        this.displayedSparks.forEach(spark => {
          if (spark.id === sparkId) {
            spark.isNew = true;
          }
        });
        this.pinnedSparks.forEach(spark => {
          if (spark.id === sparkId) {
            spark.isNew = true;
          }
        });
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    }
  }

  private async toggleSparkLike(sparkId: number, isLiked: boolean) {
    try {
      const result = await this.projectHubService.LikeSpark(sparkId, isLiked);
      if (!result.hasError) {
        // Update the local property in both arrays
        this.displayedSparks.forEach(spark => {
          if (spark.id === sparkId) {
            spark.liked = isLiked;
            // Also mark as read since liking is an interaction
            if (spark.isNew) {
              spark.isNew = false;
            }
            // Update newSparks observable for this board
            const newCount = this.displayedSparks.filter(s => s.isNew).length;
            let currentMap: { [boardId: number]: number } = {};
            this.projectHubService.newSparksMap$
              .subscribe(map => {
                currentMap = map;
              })
              .unsubscribe();
            this.projectHubService.setNewSparksMap({
              ...currentMap,
              [this.boardId]: newCount,
            });
          }
        });
        this.pinnedSparks.forEach(spark => {
          if (spark.id === sparkId) {
            spark.liked = isLiked;
            // Also mark as read since liking is an interaction
            if (spark.isNew) {
              spark.isNew = false;
            }
          }
        });
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage($localize`:@@internalServerError:Internal server error`);
    }
  }

  onFilterChanged(filterState: SparkBoardFilterState) {
    this.filterState = filterState;
    this.loadBoardData();
  }

  // Check if any filters are active
  get hasActiveFilters(): boolean {
    return ((this.filterState.period.from && this.filterState.period.to) ||
      this.filterState.tags.length > 0 ||
      (this.filterState.person !== null && this.filterState.person !== undefined) ||
      this.filterState.unread ||
      (this.searchTerm && this.searchTerm.trim().length > 0)) as boolean;
  }

  // Get appropriate empty state message
  get emptyStateMessage(): string {
    if (this.hasActiveFilters) {
      return $localize`:@@noSparksFoundWithFilters:No sparks found matching your current filters. Try adjusting your search criteria or clearing filters.`;
    }
    return $localize`:@@noSparksFound:No sparks have been posted yet. Be the first to share an idea or update!`;
  }

  // Check if we should show empty state (no sparks and no pinned sparks)
  get shouldShowEmptyState(): boolean {
    return (
      this.displayedSparks.length === 0 && this.pinnedSparks.length === 0 && !this.loadingSparks && !this.loadingBoard
    );
  }

  // Clear all filters and search
  clearAllFilters() {
    this.filterState = {
      period: { from: null, to: null },
      tags: [],
      person: null,
      unread: false,
    };
    this.searchTerm = '';
    this.loadBoardData();
  }
}
